/**
 * useTimezoneFormatter
 * ====================
 * A centralized React hook for formatting dates/times across the entire app.
 *
 * Behavior:
 * - For tenant users: reads `tenant.timezone` from the auth context and formats
 *   all dates in that configured timezone (e.g. "Asia/Kolkata").
 * - For normal users (no tenant): falls back to the browser's local system timezone.
 *
 * Usage:
 *   const { formatDate, formatTime, formatWithTz } = useTimezoneFormatter();
 *   <span>{formatDate(row.created_at)}</span>
 */

import { useContext, useCallback } from 'react';
import { useAuth } from '../context/useAuth';
import {
    formatDateTime,
    formatToLocalTime24h,
    formatWithTimezone,
} from '../utils/dateTime';

export function useTimezoneFormatter() {
    // useAuth() returns the full auth context including user object and browser timezone
    let auth;
    try {
        auth = useAuth();
    } catch {
        auth = null;
    }

    // Priority:
    // 1. tenant.timezone from the user profile (set by admin in tenant settings)
    // 2. browser-detected IANA timezone (auto-detected on login, stored in auth context)
    // 3. undefined → browser local timezone (Intl.DateTimeFormat default)
    const tenantTimezone =
        auth?.user?.tenantTimezone ||   // Tenant-level preference
        null;

    const userPreferenceTimezone =
        auth?.user?.timezone ||         // Individual user-level preference
        null;

    const browserTimezone = auth?.timezone || null;  // Auto-detected (e.g. "Asia/Kolkata")

    // Use priority: 1. Tenant, 2. User, 3. Browser
    const tz = tenantTimezone || userPreferenceTimezone || browserTimezone || undefined;

    /** Format UTC date value to full date + time string */
    const formatDate = useCallback(
        (dateVal) => formatDateTime(dateVal, tz),
        [tz]
    );

    /** Format UTC date value to time-only string */
    const formatTime = useCallback(
        (dateVal) => formatToLocalTime24h(dateVal, tz),
        [tz]
    );

    /** Format UTC date value with timezone abbreviation (e.g. "26/03/2026, 18:00 IST") */
    const formatWithTz = useCallback(
        (dateVal) => formatWithTimezone(dateVal, tz),
        [tz]
    );

    return { formatDate, formatTime, formatWithTz, timezone: tz };
}
